/**
 * Database Block Transformer
 *
 * A database block names its root document; rows, cells and their blobs live
 * in its row shards, which the database export carries, not the block.
 */

import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type {
  BlockSnapshotLeaf,
  FromSnapshotPayload,
  SnapshotNode,
  ToSnapshotPayload,
} from '@blocksuite/store';
import { BaseBlockTransformer } from '@blocksuite/store';
import {
  databaseShardDirectorySnapshotProps,
  readDatabaseShardDirectory,
  withoutDatabaseShardDirectory,
} from '@streamlain/database-identity';

import type { DatabaseBlockProps } from './database-model.js';

export class DatabaseBlockTransformer extends BaseBlockTransformer<DatabaseBlockProps> {
  override toSnapshot(
    snapshot: ToSnapshotPayload<DatabaseBlockProps>
  ): BlockSnapshotLeaf {
    const work = this.toSnapshotWork(snapshot);
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  }

  override *toSnapshotWork(
    snapshot: ToSnapshotPayload<DatabaseBlockProps>
  ): Generator<void, BlockSnapshotLeaf> {
    const snapshotRet = yield* super.toSnapshotWork(snapshot);
    // A snapshot is JSON and carries the complete directory, read from the
    // directory parts of the model's workspace, row fingerprints as text.
    const manifest = readDatabaseShardDirectory(snapshot.model);
    if (manifest)
      Object.assign(
        snapshotRet.props,
        databaseShardDirectorySnapshotProps(manifest)
      );
    return snapshotRet;
  }

  override async fromSnapshot(
    payload: FromSnapshotPayload
  ): Promise<SnapshotNode<DatabaseBlockProps>> {
    const snapshotRet = await super.fromSnapshot(payload);
    // One database model: a database snapshot names its root document.
    if (!snapshotRet.props.databaseRootDocId) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database snapshot "${snapshotRet.id}" names no root document.`
      );
    }
    // The carried list describes the database the snapshot was taken from;
    // the restored block names its root, which stores the directory.
    snapshotRet.props = withoutDatabaseShardDirectory(snapshotRet.props);
    return snapshotRet;
  }
}
